import PropTypes from 'prop-types';
import React from 'react';
import {
  NEW_IMAGE_REGEX,
  JPG_QUALITY,
  JPG_QUALITY_HQ,
  RESPONSIVE_IMG_WIDTH_PX,
  RESPONSIVE_IMG_HEIGHT_PX
} from '../../constants';
import {
  shouldRenderResponsiveImg,
  shouldForceImgAsJpg,
  shouldForceImgAsWebP,
  getSafeDimensionsForOpener,
  getSafeDimensionsForSection
} from '../../utils/images';
import renderClientSideOnly from '../hocs/renderClientSideOnly';
import {omit, get} from 'lodash';
import { parse as parseUrl, format as formatUrl } from 'url';


/*
 * FastlyResponsiveImg never renders server-side. This is so that we can
 * change the format for certain browsers, without triggering
 * a double image request.
 *
 * We accomplish this by decorating FastlyResponsiveImg with the
 * `renderClientSideOnly()` HOC at the bottom of this file.
 */

export const FastlyResponsiveImg = (props, { location }) => {
  if (typeof window === 'undefined') {
    throw new Error(
      'Tried to render a FastlyResponsiveImg on the server-side.'
      + ' This should never happen.'
    );
  }

  const {
    src, format, optimizeOrientation, width, height,
    original_width_px, original_height_px
  } = props;

  const delegatedProps = omit(props, [
    'src', 'format', 'optimizeOrientation', 'width', 'height',
    'original_width_px', 'original_height_px'
  ]);

  if (!src) {
    return <img src={src} {...delegatedProps} />;
  }

  const match = src.match(NEW_IMAGE_REGEX);
  if (!match) {
    return <img src={src} {...delegatedProps} />;
  }

  // Determine the file format
  const srcExtension = match[3];
  const forceImgAsWebP = shouldForceImgAsWebP(location, srcExtension);
  const forceImgAsJpg = shouldForceImgAsJpg(srcExtension, location);
  const finalFormat = determineFinalFormat(srcExtension, format, forceImgAsJpg, forceImgAsWebP);
  const formatQuality = determineFormatQuality(finalFormat, format, forceImgAsJpg, forceImgAsWebP);

  if (!shouldRenderResponsiveImg(finalFormat)) {
    return <img src={src} {...delegatedProps} />;
  }


  let optimizedWidth = width;
  let optimizedHeight = height;

  /*
   * Fallback for images which we want to render the full-sized
   * asset for, such as full-bleed images on desktop.
   */
  if (!width || !height) {
    optimizedWidth = original_width_px;
    optimizedHeight = original_height_px;
  }

  // Override height and width when optimizeOrientation is set
  if (optimizeOrientation) {
    const imageRatio = width / height;
    if (width > height) {
      optimizedWidth = 2 * RESPONSIVE_IMG_WIDTH_PX;
      optimizedHeight = Math.round(optimizedWidth / imageRatio);
    } else {
      optimizedHeight = 2 * RESPONSIVE_IMG_HEIGHT_PX;
      optimizedWidth = Math.round(optimizedHeight * imageRatio);
    }
  }

  return (
    <img
      src={getSrc(
        src, optimizedWidth, optimizedHeight, formatQuality,
        finalFormat, original_width_px, original_height_px
      )}
      srcSet={getSrcSet(
        src, optimizedWidth, optimizedHeight, formatQuality,
        finalFormat, original_width_px, original_height_px
      )}
      {...delegatedProps}
    />
  );
};

/*
 * Returns the image format that we want to convert this image to.
 * Ref: https://docs.fastly.com/api/imageopto/format
 */
export function determineFinalFormat(srcExtension, format, forceImgAsJpg, forceImgAsWebP) {
  if (forceImgAsWebP) {
    return 'webp';
  }

  switch (format) {
    case 'alwaysForceJpgHq':
      return 'jpg';
    case 'alwaysUseSrcExtension':
      return srcExtension;
    default:
      return forceImgAsJpg ? 'jpg' : srcExtension;
  }
}

/*
 * Returns the quality from 0-100 to use for the image format.
 * If the image is not webp or jpg, we return false.
 * Ref: https://docs.fastly.com/api/imageopto/quality
 */
function determineFormatQuality(finalExtension, format, forceImgAsJpg, forceImgAsWebP) {
  if (!/jpe?g/i.test(finalExtension) && !forceImgAsWebP) {
    // Note: We do not pass a 'quality' parameter to fastly I/O in this case.
    return false;
  }

  if (format === 'alwaysForceJpgHq' || forceImgAsJpg || forceImgAsWebP) {
    return JPG_QUALITY_HQ;
  }

  return JPG_QUALITY;
}

/*
 * Returns the resized URL string for src.
 * Resize using Fastly Image Optimizer
 * Ref: https://docs.fastly.com/api/imageopto
 */
function getSrc(src, width, height, formatQuality, format, ogWidth, ogHeight) {
  /*
   * We do not pass in the ogWidth and ogHeight for Openers because their
   * dimensions are not passed down in the API.
   * See: components/article/opener_image.js
   */
  const { safeWidth, safeHeight } = ogWidth && ogHeight
    ? getSafeDimensionsForSection(width, height, ogWidth, ogHeight)
    : getSafeDimensionsForOpener(width, height);

  const fastlyIOParams = {
    format: format,
    width: Math.round(safeWidth),
    height: Math.round(safeHeight)
  };

  if (formatQuality) {
    fastlyIOParams.quality = formatQuality;
  }

  // Maintain cropping
  const parsedUrl = parseUrl(src, true);
  if (get(parsedUrl, 'query.crop', false)) {
    fastlyIOParams.crop = parsedUrl.query.crop;
  }

  // `query` is only respected if `search` is null
  return formatUrl({
    ...parsedUrl,
    query: {
      ...fastlyIOParams
    },
    search: null
  });
}

/*
 * Returns the resized URL string for srcSet at 2x resolution.
 * Resize using Fastly Image Optimizer
 * Ref: https://docs.fastly.com/api/imageopto
 */
function getSrcSet(src, width, height, formatQuality, format, ogWidth, ogHeight) {
  /*
   * We do not pass in the ogWidth and ogHeight for Openers because their
   * dimensions are not passed down in the API.
   * See: components/article/opener_image.js
   */
  const { safeWidth, safeHeight } = ogWidth && ogHeight
    ? getSafeDimensionsForSection(width * 2, height * 2, ogWidth, ogHeight)
    : getSafeDimensionsForOpener(width * 2, height * 2);

  const fastlyIOParams = {
    format: format,
    width: Math.round(safeWidth),
    height: Math.round(safeHeight)
  };

  if (formatQuality) {
    fastlyIOParams.quality = formatQuality;
  }

  // Maintain cropping
  const parsedUrl = parseUrl(src, true);
  if (get(parsedUrl, 'query.crop', false)) {
    fastlyIOParams.crop = parsedUrl.query.crop;
  }

  // `query` is only respected if `search` is null
  return formatUrl({
    ...parsedUrl,
    query: {
      ...fastlyIOParams
    },
    search: null
  }) + ' 2x';
}

FastlyResponsiveImg.propTypes = {
  /*
   * Props passed in, but not named here, will be forwarded
   * to the <img> component
   */
  width: PropTypes.number,
  height: PropTypes.number,
  src: PropTypes.string,
  original_width_px: PropTypes.number,
  original_height_px: PropTypes.number,
  format: PropTypes.oneOf([
    'alwaysForceJpgHq',
    'alwaysUseSrcExtension'
  ]),
  optimizeOrientation: PropTypes.bool
};

FastlyResponsiveImg.defaultProps = {
  /*
   * The "alt" must be explicitly set here b/c screenreaders will
   * utilize the src tag by default if an alt tag is not present.
   */
  alt: ''
};

FastlyResponsiveImg.contextTypes = {
  location: PropTypes.object.isRequired
};

/*
 * See comment at top of file for why this renders client-side only.
 */
export default renderClientSideOnly()(FastlyResponsiveImg);
